<template>
  <div class="scan-progress-container" v-if="showProgress">
    <div class="scan-progress-header">
      <span class="scan-title">{{ getProgressTitle() }}</span>
      <span class="scan-percentage">{{ progressPercentage }}%</span>
    </div>
    <div class="progress-bar-container">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
      </div>
    </div>
    <div class="scan-details" v-if="!batchFiles || batchFiles.length === 0">
      <span class="page-info" v-if="progressMessage">{{ progressMessage }}</span>
      <span class="page-info" v-else-if="currentPage >= 0 && totalPages > 0">
        Processing pages {{ currentPage }} of {{ totalPages }}
      </span>
    </div>

    <!-- Batch summary -->
    <div v-if="taskType === 'batch-upload' && batchStats" class="batch-summary">
      <div class="batch-summary-counts">
        <span>
          Processed {{ batchStats.processed_files || 0 }}/{{ batchStats.total_files || 0 }}
        </span>
        <span class="batch-count-success">
          {{ batchStats.successful_files || 0 }} successful
        </span>
        <span
          class="batch-count-failed"
          :class="{ 'has-failures': (batchStats.failed_files || 0) > 0 }"
        >
          {{ batchStats.failed_files || 0 }} failed
        </span>
      </div>
      <div v-if="batchStats.current_file_name" class="batch-current-file">
        Current:
        <strong>
          {{ batchStats.current_file_name }}
          <template v-if="Number.isInteger(batchStats.current_file_index)">
            ({{ batchStats.current_file_index + 1 }}/{{ batchStats.total_files || '?' }})
          </template>
        </strong>
      </div>
      <div v-if="progressMessage" class="batch-progress-message">
        {{ progressMessage }}
      </div>
    </div>

    <!-- Individual File Progress for Batch Uploads -->
    <div v-if="taskType === 'batch-upload' && batchFiles && batchFiles.length > 0" class="batch-files-progress">
      <h5>Individual File Progress</h5>
      <div class="files-progress-list">
        <div
          v-for="file in batchFiles"
          :key="file.upload_id || file.file_index"
          class="file-progress-item"
          :class="[getFileStatusClass(file.status), { 'is-current': file.is_current }]"
        >
          <div class="file-progress-header">
            <span class="file-name">{{ file.file_name }}</span>
            <span class="file-status" :class="getFileStatusClass(file.status)" :title="getStatusText(file.status)">
              <i
                :class="[
                  getStatusIcon(file.status),
                  { 'status-icon-spin': isProcessingStatus(file.status) }
                ]"
              ></i>
            </span>
          </div>
          <div
            class="file-progress-bar-container"
            v-if="isActiveFileStatus(file.status)"
          >
            <div class="file-progress-bar">
              <div class="file-progress-fill" :style="{ width: (file.progress || 0) + '%' }"></div>
            </div>
            <div class="file-progress-details">
              <span v-if="file.progress || file.progress === 0">{{ file.progress }}%</span>
              <span v-if="file.total_pages > 0">
                Page {{ file.current_page || 0 }}/{{ file.total_pages }}
              </span>
            </div>
          </div>
          <div class="file-progress-message" v-if="file.error || file.message">
            {{ file.error || file.message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ScanProgressBar',
  props: {
    progressPercentage: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 0
    },
    totalPages: {
      type: Number,
      default: 0
    },
    taskType: {
      type: String,
      default: 'upload'
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    progressMessage: {
      type: String,
      default: ''
    },
    batchFiles: {
      type: Array,
      default: () => []
    },
    batchStats: {
      type: Object,
      default: null
    }
  },
  methods: {
    getProgressTitle() {
      switch (this.taskType) {
        case 'upload':
          return 'Parsing File';
        case 'batch-upload':
          return 'Parsing Files';
        case 'processing':
          return 'Processing File';
        case 'scan':
          return 'Scanning File';
        case 'rescan':
          return 'Rescanning File';
        default:
          return 'Processing';
      }
    },

    isActiveFileStatus(status) {
      return ['processing', 'scanning', 'extracting', 'indexing', 'uploaded'].includes(status);
    },

    isProcessingStatus(status) {
      return this.isActiveFileStatus(status);
    },

    getFileStatusClass(status) {
      switch (status) {
        case 'completed':
          return 'status-completed';
        case 'processing':
        case 'scanning':
        case 'extracting':
        case 'indexing':
        case 'uploaded':
          return 'status-processing';
        case 'failed':
        case 'error':
          return 'status-failed';
        case 'pending':
          return 'status-pending';
        default:
          return 'status-pending';
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'completed':
          return 'Completed';
        case 'processing':
        case 'uploaded':
          return 'Processing';
        case 'scanning':
          return 'Scanning';
        case 'extracting':
          return 'Extracting';
        case 'indexing':
          return 'Indexing';
        case 'failed':
        case 'error':
          return 'Failed';
        case 'pending':
          return 'Pending';
        default:
          return 'Unknown';
      }
    },

    getStatusIcon(status) {
      if (status === 'completed') return 'las la-check';
      if (['processing', 'scanning', 'extracting', 'indexing', 'uploaded'].includes(status)) {
        return 'las la-sync';
      }
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.scan-progress-container {
  padding: 1rem 1.15rem;
  margin: 0;
  width: 100%;
  max-width: 100%;
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 12px;
}

.scan-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.scan-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
}

.scan-percentage {
  font-size: 0.9rem;
  font-weight: 600;
  color: $color-primary;
}

.progress-bar-container {
  margin-bottom: 0.75rem;
}

.progress-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: $color-primary;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.scan-details {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #6c757d;
}

.batch-summary {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.batch-summary-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  font-size: 0.8rem;
  color: #4a5568;
}

.batch-count-success {
  color: #15803d;
}

.batch-count-failed {
  color: #6c757d;

  &.has-failures {
    color: #b91c1c;
    font-weight: 600;
  }
}

.batch-current-file,
.batch-progress-message {
  font-size: 0.8rem;
  color: #6c757d;
}

.batch-current-file strong {
  color: #2d3748;
  font-weight: 600;
}

.batch-files-progress {
  margin-top: 1rem;
  padding-top: 1rem;

  h5 {
    margin: 0 0 0.75rem 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
  }
}

.files-progress-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  width: 100%;
}

.file-progress-item {
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  background: #f8f9fa;
  min-width: 0;
  border: 1px solid transparent;

  &.is-current {
    border-color: $color-primary;
    box-shadow: 0 0 0 1px rgba($color-primary, 0.2);
  }
}

.file-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.file-name {
  font-size: 0.85rem;
  color: #333;
  flex: 1;
  word-break: break-word;
}

.file-status {
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;

  i {
    font-size: 0.95rem;
    color: #6b7280;
  }

  &.status-completed i {
    color: #16a34a;
  }

  &.status-processing i {
    color: #3b82f6;
  }

  &.status-failed i {
    color: #dc2626;
  }

  &.status-pending i {
    color: #6b7280;
  }
}

.status-icon-spin {
  animation: status-icon-spin 1.2s linear infinite;
}

@keyframes status-icon-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.file-progress-bar-container {
  margin-top: 0.5rem;

  .file-progress-bar {
    height: 6px;
    background: #e9ecef;
    border-radius: 3px;
    overflow: hidden;
  }

  .file-progress-fill {
    height: 100%;
    background: $color-primary;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
}

.file-progress-details,
.file-progress-message {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.file-progress-details {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}
</style>
